import type { DiGraph } from "../../graph/digraph.js";
import type { Question } from "../../types.js";
import { buildCurriculumGraph, CURRICULUM_NODE_IDS } from "./curriculum.js";
import {
  COPY,
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  getCopy,
  type Locale,
  type LocaleStrings,
  type StemTemplate,
} from "./conceptCopy.js";
import { SEED_QUESTIONS } from "./seed.js";
import { BACKPACK_DECK } from "./decks/backpack.js";
import { SPOTLIE_DECK } from "./decks/spotlie.js";
import { REDACT_DECK } from "./decks/redact.js";
import { AI_LITERACY_BACKPACK_DECK_EL } from "./decks/backpackLocaleEl.js";
import { AI_LITERACY_SPOTLIE_DECK_EL } from "./decks/spotlieLocaleEl.js";
import { AI_LITERACY_REDACT_DECK_EL } from "./decks/redactLocaleEl.js";
import type { LinearQuest } from "../../quest/linearQuest.js";
import { applyAiLiteracySeedLocale } from "./applySeedLocale.js";

/** PWA navigation targets (router + home cards). */
export type PwaView = "home" | "mcq" | "backpack" | "spotlie" | "redact";

/**
 * Bundles all content for one learnable subject: curriculum, copy, seed bank, and
 * mini-game decks. Engine code stays subject-agnostic; the PWA picks one pack.
 */
export type SubjectPack = {
  id: string;
  buildGraph: () => DiGraph;
  conceptIds: readonly string[];
  /** Locale tables + stem templates for MCQ generation. */
  copy: typeof COPY;
  getCopy: (locale?: Locale) => LocaleStrings;
  defaultLocale: typeof DEFAULT_LOCALE;
  supportedLocales: readonly Locale[];
  seedQuestions: readonly Question[];
  /** Rewrite seed `text` / `options` for MCQ display (ratings still merged from persistence). */
  applySeedLocale?: (question: Question, locale: Locale) => Question;
  /** Optional per-locale mini-game deck text rewrite (defaults to canonical `decks`). */
  localizeDecks?: (locale: Locale) => {
    readonly backpack: typeof BACKPACK_DECK;
    readonly spotlie: typeof SPOTLIE_DECK;
    readonly redact: typeof REDACT_DECK;
  };
  /**
   * Maps a weak concept (from `recommendNextConcept`) to the most relevant practice
   * format for the home “Suggested” badge.
   */
  recommendViewForConcept: (conceptId: string | null) => PwaView | null;
  /** Optional linear path (M.5); first step below `minElo` is current. */
  quest?: LinearQuest;
  decks: {
    readonly backpack: typeof BACKPACK_DECK;
    readonly spotlie: typeof SPOTLIE_DECK;
    readonly redact: typeof REDACT_DECK;
  };
};

function recommendViewForAiLiteracy(conceptId: string | null): PwaView | null {
  if (!conceptId) return null;
  if (conceptId === "privacy" || conceptId === "adversarial") return "redact";
  if (conceptId === "prompting" || conceptId === "context") return "backpack";
  if (conceptId === "trust_reasoning" || conceptId === "evidence" || conceptId === "model_theory") {
    return "spotlie";
  }
  return "mcq";
}

/** Default shipped subject: AI usage & safety literacy (current PWA content). */
export const AI_LITERACY_PACK: SubjectPack = {
  id: "ai_literacy",
  buildGraph: buildCurriculumGraph,
  conceptIds: CURRICULUM_NODE_IDS,
  copy: COPY,
  getCopy,
  defaultLocale: DEFAULT_LOCALE,
  supportedLocales: SUPPORTED_LOCALES,
  seedQuestions: SEED_QUESTIONS,
  applySeedLocale: applyAiLiteracySeedLocale,
  recommendViewForConcept: recommendViewForAiLiteracy,
  quest: {
    id: "starter",
    steps: [
      { conceptId: "foundations", minElo: 1280 },
      { conceptId: "trust_reasoning", minElo: 1280 },
      { conceptId: "prompting", minElo: 1280 },
      { conceptId: "context", minElo: 1300 },
    ],
  },
  decks: {
    backpack: BACKPACK_DECK,
    spotlie: SPOTLIE_DECK,
    redact: REDACT_DECK,
  },
  localizeDecks: (locale) => {
    if (locale !== "el") {
      return {
        backpack: BACKPACK_DECK,
        spotlie: SPOTLIE_DECK,
        redact: REDACT_DECK,
      };
    }
    return {
      backpack: AI_LITERACY_BACKPACK_DECK_EL,
      spotlie: AI_LITERACY_SPOTLIE_DECK_EL,
      redact: AI_LITERACY_REDACT_DECK_EL,
    };
  },
};

export type { Locale, LocaleStrings, StemTemplate };
